import { useEffect, useRef, useState } from 'react'
import { useTheme } from '../context/ThemeContext'
import { getThemeLabel, MAX_CUSTOM_THEMES } from '../lib/customThemes'
import {
  applyThemeColorOverrides,
  snapshotThemeForSave,
  bakeActiveMasterState,
  buildThemeColors,
  colorsToCssBlock,
  buildReferencePalette,
  DEFAULT_THEME_MASTER,
  emitThemeColorsChanged,
  readThemeBaselineColors,
  readThemeColorOverrides,
  resolveThemeColorEditingState,
  THEME_COLOR_GROUPS,
  writeThemeColorOverrides,
} from '../lib/themeColorTokens'
import { HUB_TOOL_IDS, formatCopySnippet, useHubToolEvents } from '../lib/hubTools'
import { normalizeLightHex } from '../lib/colorPickerUtils'
import HubToolWindow from './HubToolWindow'
import ThemeColorPickerWindow from './ThemeColorPickerWindow'

const SliderRow = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format = (next) => String(next),
}) => (
  <label className="block space-y-1.5">
    <div className="flex items-center justify-between gap-2 text-[11px] uppercase tracking-wide text-white/70">
      <span>{label}</span>
      <span className="font-mono text-white/90">{format(value)}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
      onPointerDown={(event) => event.stopPropagation()}
      className="w-full cursor-pointer accent-fuchsia-400"
      aria-label={label}
    />
  </label>
)

const ColorRow = ({ label, value, onChange, onOpenPicker, isPickerOpen }) => (
  <div className="block space-y-1.5">
    <div className="flex items-center justify-between gap-2 text-[11px] uppercase tracking-wide text-white/70">
      <span>{label}</span>
      <span className="font-mono text-[10px] text-white/90">{value}</span>
    </div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onOpenPicker}
        className={`h-9 w-12 shrink-0 cursor-pointer rounded border p-0.5 ${
          isPickerOpen ? 'border-fuchsia-400/70 ring-1 ring-fuchsia-400/40' : 'border-white/20'
        }`}
        style={{ backgroundColor: value }}
        aria-label={`Open ${label} color picker`}
        aria-pressed={isPickerOpen}
      />
      <input
        type="text"
        defaultValue={value}
        key={value}
        onBlur={(event) => onChange(normalizeLightHex(event.target.value, value))}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return
          event.currentTarget.blur()
        }}
        className="min-w-0 flex-1 rounded-md border border-white/20 bg-black/50 px-2 py-1.5 font-mono text-[11px] uppercase text-white/90"
        spellCheck={false}
        aria-label={`${label} hex code`}
      />
    </div>
  </div>
)

const ThemeColorTuner = ({ defaultOpen = false, columnIndex = 3, showOwnerControls = false }) => {
  const {
    theme,
    customThemes,
    createCustomTheme,
    deleteCustomTheme,
    saveCustomThemeColors,
    persistActiveTheme,
    canCreateCustomTheme,
    isCustomTheme,
  } = useTheme()
  const baseColorsRef = useRef(null)
  const referenceColorsRef = useRef(null)
  const [master, setMaster] = useState(() => ({ ...DEFAULT_THEME_MASTER }))
  const [overrides, setOverrides] = useState({})
  const [values, setValues] = useState({})
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const [themeActionMessage, setThemeActionMessage] = useState('')
  const [pickerTarget, setPickerTarget] = useState(null)
  const [pickerMounted, setPickerMounted] = useState(false)
  const pickerSnapshotRef = useRef(null)
  const previewFrameRef = useRef(0)
  const lastSavedRef = useRef({
    master: { ...DEFAULT_THEME_MASTER },
    overrides: {},
    reference: {},
    values: {},
  })
  const themeRef = useRef(theme)

  const composeColors = (reference, nextMaster, nextOverrides) =>
    buildThemeColors(reference, nextMaster, nextOverrides)

  const applyLive = (nextMaster, nextOverrides, nextReference = null) => {
    if (nextReference) {
      referenceColorsRef.current = nextReference
      baseColorsRef.current = nextReference
    }
    const reference = referenceColorsRef.current || readThemeBaselineColors()
    referenceColorsRef.current = reference
    const nextValues = composeColors(reference, nextMaster, nextOverrides)
    setMaster(nextMaster)
    setOverrides(nextOverrides)
    setValues(nextValues)
    applyThemeColorOverrides(nextValues)
    emitThemeColorsChanged()
  }

  const reloadFromDocument = () => {
    const factoryBase = readThemeBaselineColors()
    baseColorsRef.current = factoryBase
    const saved = readThemeColorOverrides(themeRef.current)
    const {
      master: nextMaster,
      overrides: nextOverrides,
      reference,
    } = resolveThemeColorEditingState(factoryBase, saved)
    referenceColorsRef.current = reference
    const nextValues = composeColors(reference, nextMaster, nextOverrides)
    setMaster(nextMaster)
    setOverrides(nextOverrides)
    setValues(nextValues)
    applyThemeColorOverrides(nextValues)
    emitThemeColorsChanged()
    return { base: factoryBase, reference, master: nextMaster, overrides: nextOverrides, values: nextValues }
  }

  useEffect(() => {
    themeRef.current = theme
    const next = reloadFromDocument()
    lastSavedRef.current = {
      master: { ...next.master },
      overrides: { ...next.overrides },
      reference: { ...next.reference },
      values: { ...next.values },
    }
    setPickerTarget(null)
    pickerSnapshotRef.current = null
  }, [theme])

  const handleMasterChange = (key, value) => {
    const nextMaster = { ...master, [key]: value }
    applyLive(nextMaster, overrides)
  }

  const commitMasterToOverrides = () =>
    bakeActiveMasterState(referenceColorsRef.current, master, overrides, values)

  const handleResetMaster = () => {
    applyLive({ ...DEFAULT_THEME_MASTER }, overrides)
  }

  const handleChange = (key, value) => {
    const baked = commitMasterToOverrides()
    const nextOverrides = { ...baked.overrides, [key]: value }
    applyLive(baked.master, nextOverrides)
  }

  const handleClearOverride = (key) => {
    const nextOverrides = { ...overrides }
    delete nextOverrides[key]
    applyLive(master, nextOverrides)
  }

  const restorePickerSnapshot = () => {
    if (previewFrameRef.current) {
      window.cancelAnimationFrame(previewFrameRef.current)
      previewFrameRef.current = 0
    }
    const snap = pickerSnapshotRef.current
    if (!snap) return
    applyLive(snap.master, snap.overrides)
    pickerSnapshotRef.current = null
  }

  const handlePreviewPicker = (hex) => {
    if (!pickerTarget || !pickerSnapshotRef.current) return
    const snap = pickerSnapshotRef.current
    const previewOverrides = { ...snap.overrides, [pickerTarget.key]: hex }
    const nextValues = composeColors(referenceColorsRef.current, snap.master, previewOverrides)

    if (previewFrameRef.current) {
      window.cancelAnimationFrame(previewFrameRef.current)
    }

    previewFrameRef.current = window.requestAnimationFrame(() => {
      previewFrameRef.current = 0
      setValues(nextValues)
      applyThemeColorOverrides(nextValues)
      emitThemeColorsChanged()
    })
  }

  const handleCommitPicker = (hex) => {
    if (previewFrameRef.current) {
      window.cancelAnimationFrame(previewFrameRef.current)
      previewFrameRef.current = 0
    }
    pickerSnapshotRef.current = null
    if (pickerTarget) {
      handleChange(pickerTarget.key, hex)
    }
    setPickerTarget(null)
  }

  const handleCancelPicker = () => {
    restorePickerSnapshot()
    setPickerTarget(null)
  }

  const handleOpenPicker = (field) => {
    setPickerMounted(true)
    if (pickerTarget?.key === field.key) {
      handleCancelPicker()
      return
    }
    if (pickerTarget) {
      restorePickerSnapshot()
    }
    pickerSnapshotRef.current = {
      master: { ...master },
      overrides: { ...overrides },
    }
    setPickerTarget({
      key: field.key,
      label: field.label,
      initialHex: values[field.key] || '#000000',
      // Exact numbers, so matching never has to sample display-space pixels.
      matchOptions: THEME_COLOR_GROUPS.flatMap((group) =>
        group.fields
          .filter((option) => option.key !== field.key)
          .map((option) => ({
            key: option.key,
            label: option.label,
            groupLabel: group.label,
            hex: values[option.key] || '#000000',
          }))
      ),
    })
  }

  const showThemeActionMessage = (message) => {
    setThemeActionMessage(message)
    window.setTimeout(() => setThemeActionMessage(''), 3000)
  }

  const handleSave = async () => {
    try {
      const baked = commitMasterToOverrides()
      const composed = composeColors(referenceColorsRef.current, baked.master, baked.overrides)
      const payload = snapshotThemeForSave(composed)
      writeThemeColorOverrides(theme, payload)
      if (isCustomTheme(theme)) {
        saveCustomThemeColors(theme, composed, payload)
      }
      const reference = buildReferencePalette(
        readThemeBaselineColors(),
        payload.overrides,
      )
      const runtimeMaster = { ...DEFAULT_THEME_MASTER }
      const runtimeOverrides = {}
      applyLive(runtimeMaster, runtimeOverrides, reference)
      lastSavedRef.current = {
        master: { ...runtimeMaster },
        overrides: { ...runtimeOverrides },
        reference: { ...reference },
        values: { ...composed },
      }
      persistActiveTheme()
      setSaveError(false)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2000)
    } catch {
      setSaveError(true)
      window.setTimeout(() => setSaveError(false), 4000)
    }
  }

  const handleCreateCustomTheme = () => {
    if (!canCreateCustomTheme) {
      showThemeActionMessage(`You can save up to ${MAX_CUSTOM_THEMES} custom themes.`)
      return
    }

    const defaultName = `Custom ${customThemes.length + 1}`
    const name = window.prompt('Name your new theme', defaultName)
    if (name === null) return
    if (!name.trim()) {
      showThemeActionMessage('Theme name is required.')
      return
    }

    const baked = commitMasterToOverrides()
    const composed = composeColors(referenceColorsRef.current, baked.master, baked.overrides)
    const payload = snapshotThemeForSave(composed)
    const result = createCustomTheme({
      label: name.trim(),
      sourceThemeId: theme,
      colors: composed,
      colorSavePayload: payload,
    })

    if (!result.ok) {
      showThemeActionMessage(`You can save up to ${MAX_CUSTOM_THEMES} custom themes.`)
      return
    }

    showThemeActionMessage(`Created "${name.trim()}".`)
    persistActiveTheme()
  }

  const handleDeleteCustomTheme = () => {
    if (!isCustomTheme(theme)) return

    const label = getThemeLabel(theme)
    const confirmed = window.confirm(`Delete custom theme "${label}"?`)
    if (!confirmed) return

    deleteCustomTheme(theme)
    showThemeActionMessage(`Deleted "${label}".`)
  }

  const handleCopy = async () => {
    const themeLabel = getThemeLabel(theme)
    const profile = isCustomTheme(theme) ? `custom: ${themeLabel}` : themeLabel
    const target = `[data-theme='${theme}']`
    const snippet = formatCopySnippet({
      tool: 'Theme Colors',
      profile,
      target,
      body: colorsToCssBlock(theme, values),
    })
    try {
      await navigator.clipboard.writeText(snippet)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      console.log('Theme colors:\n', snippet)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleFactoryReset = () => {
    writeThemeColorOverrides(theme, null)
    const factoryBase = readThemeBaselineColors()
    referenceColorsRef.current = { ...factoryBase }
    baseColorsRef.current = factoryBase
    const nextMaster = { ...DEFAULT_THEME_MASTER }
    const nextOverrides = {}
    const nextValues = composeColors(factoryBase, nextMaster, nextOverrides)
    setMaster(nextMaster)
    setOverrides(nextOverrides)
    setValues(nextValues)
    applyThemeColorOverrides(nextValues)
    emitThemeColorsChanged()
    lastSavedRef.current = {
      master: { ...nextMaster },
      overrides: { ...nextOverrides },
      reference: { ...factoryBase },
      values: { ...nextValues },
    }
  }

  const handleRevertSaved = () => {
    applyLive(
      { ...lastSavedRef.current.master },
      { ...lastSavedRef.current.overrides },
      { ...lastSavedRef.current.reference },
    )
  }

  useHubToolEvents(HUB_TOOL_IDS.themeColors, {
    save: handleSave,
    reset: handleRevertSaved,
    copy: handleCopy,
    loadDefaults: handleFactoryReset,
  })

  useHubToolEvents(HUB_TOOL_IDS.themeColorPicker, {
    close: handleCancelPicker,
  })

  const themeLabel = getThemeLabel(theme)
  const customThemeCount = customThemes.length

  return (
    <>
      <HubToolWindow
        toolId={HUB_TOOL_IDS.themeColors}
        label="Theme Colors"
        titleClassName="text-fuchsia-300"
        borderClassName="border-fuchsia-400/40"
        defaultOpen={defaultOpen}
        columnIndex={columnIndex}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="shrink-0 space-y-3 border-b border-white/10 p-3 pb-2.5">
            <div className="space-y-0.5 text-[11px] text-white/65">
              <div>
                Theme: <span className="font-medium text-white/90">{themeLabel}</span>
                {isCustomTheme(theme) ? ' (custom)' : ''}
              </div>
              <div>
                Custom themes: <span className="font-medium text-white/90">{customThemeCount}/{MAX_CUSTOM_THEMES}</span>
              </div>
            </div>
            {themeActionMessage ? (
              <p className="text-[11px] text-fuchsia-200/90">{themeActionMessage}</p>
            ) : null}

            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-fuchsia-200/80">
                  Master
                </h3>
                <button
                  type="button"
                  onClick={handleResetMaster}
                  className="rounded border border-white/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/70 hover:bg-white/10"
                >
                  Reset master
                </button>
              </div>
              <div className="space-y-3 rounded-lg border border-fuchsia-400/20 bg-fuchsia-500/[0.06] p-2.5">
                <SliderRow
                  label="Hue"
                  value={master.hue}
                  min={-180}
                  max={180}
                  step={1}
                  onChange={(next) => handleMasterChange('hue', next)}
                  format={(next) => `${next > 0 ? '+' : ''}${next}°`}
                />
                <SliderRow
                  label="Saturation"
                  value={master.saturation}
                  min={0}
                  max={200}
                  step={1}
                  onChange={(next) => handleMasterChange('saturation', next)}
                  format={(next) => `${next}%`}
                />
                <SliderRow
                  label="Brightness"
                  value={master.brightness ?? 100}
                  min={0}
                  max={200}
                  step={1}
                  onChange={(next) => handleMasterChange('brightness', next)}
                  format={(next) => `${next}%`}
                />
              </div>
            </section>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 pt-2.5">
            <div className="flex flex-col gap-3">
              {THEME_COLOR_GROUPS.map((group) => (
                <section key={group.id} className="space-y-2">
                  <h3 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-fuchsia-200/80">
                    {group.label}
                  </h3>
                  <div className="space-y-3 rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
                    {group.fields.map((field) => (
                      <div key={field.key} className="space-y-1">
                        <ColorRow
                          label={field.label}
                          value={values[field.key] || '#000000'}
                          onChange={(next) => handleChange(field.key, next)}
                          onOpenPicker={() => handleOpenPicker(field)}
                          isPickerOpen={pickerTarget?.key === field.key}
                        />
                        {overrides[field.key] ? (
                          <button
                            type="button"
                            onClick={() => handleClearOverride(field.key)}
                            className="text-[10px] uppercase tracking-wide text-white/55 hover:text-white/80"
                          >
                            Clear pin — follow master
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2 border-t border-white/10 bg-black/95 p-3">
            <button
              type="button"
              onClick={handleSave}
              className="rounded-md border border-fuchsia-400/50 bg-fuchsia-500/20 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-fuchsia-100 hover:bg-fuchsia-500/30"
            >
              {saved ? 'Saved' : saveError ? 'Save failed' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleRevertSaved}
              className="rounded-md border border-white/20 px-3 py-1.5 text-[11px] uppercase tracking-wide text-white/80 hover:bg-white/10"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleFactoryReset}
              className="rounded-md border border-white/20 px-3 py-1.5 text-[11px] uppercase tracking-wide text-white/80 hover:bg-white/10"
            >
              Factory reset
            </button>
            {showOwnerControls ? (
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-md border border-white/20 px-3 py-1.5 text-[11px] uppercase tracking-wide text-white/80 hover:bg-white/10"
              >
                {copied ? 'Copied' : 'Copy CSS'}
              </button>
            ) : null}
            {canCreateCustomTheme ? (
              <button
                type="button"
                onClick={handleCreateCustomTheme}
                className="rounded-md border border-emerald-400/40 bg-emerald-500/15 px-3 py-1.5 text-[11px] uppercase tracking-wide text-emerald-100 hover:bg-emerald-500/25"
              >
                New theme
              </button>
            ) : (
              <button
                type="button"
                disabled
                className="cursor-not-allowed rounded-md border border-white/10 px-3 py-1.5 text-[11px] uppercase tracking-wide text-white/35"
                title={`Maximum ${MAX_CUSTOM_THEMES} custom themes`}
              >
                New theme
              </button>
            )}
            {isCustomTheme(theme) ? (
              <button
                type="button"
                onClick={handleDeleteCustomTheme}
                className="rounded-md border border-red-400/40 bg-red-500/10 px-3 py-1.5 text-[11px] uppercase tracking-wide text-red-100 hover:bg-red-500/20"
              >
                Delete theme
              </button>
            ) : null}
          </div>
        </div>
      </HubToolWindow>

      {pickerMounted ? (
        <ThemeColorPickerWindow
          visible={Boolean(pickerTarget)}
          fieldLabel={pickerTarget?.label || 'Color'}
          initialValue={pickerTarget?.initialHex || '#000000'}
          matchOptions={pickerTarget?.matchOptions || []}
          onPreview={handlePreviewPicker}
          onCommit={handleCommitPicker}
          onCancel={handleCancelPicker}
          columnIndex={columnIndex + 1}
        />
      ) : null}
    </>
  )
}

export default ThemeColorTuner
